import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Palette, Settings, Star } from 'lucide-react';
import type { MediaBrandProfile } from './types';

export interface BrandProfilePickerProps {
  brands: MediaBrandProfile[];
  loading?: boolean;
  onSelect: (brand: MediaBrandProfile) => void;
  onManage: () => void;
  accentColor?: string;
  /** Compact mode renders a smaller trigger for inline use in toolbars */
  compact?: boolean;
}

export function BrandProfilePicker({
  brands,
  loading,
  onSelect,
  onManage,
  accentColor = '#7c3aed',
  compact = false,
}: BrandProfilePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const defaultBrand = brands.find(b => b.is_default);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={
          compact
            ? 'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all bg-slate-800 text-slate-300 border border-slate-700 hover:border-slate-500'
            : 'w-full flex items-center gap-2 py-2.5 px-3 border-2 border-dashed border-slate-700 hover:border-slate-500 rounded-xl text-sm text-slate-400 hover:text-slate-300 transition-all'
        }
      >
        <Palette size={compact ? 14 : 16} />
        <span className="truncate">
          {brands.length === 0 ? 'No brands yet' : 'Load from Brand'}
        </span>
        <ChevronDown size={14} className={`ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1.5 w-72 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top-left">
          {loading ? (
            <div className="px-4 py-6 text-center text-xs text-slate-500">Loading brands...</div>
          ) : brands.length === 0 ? (
            <div className="px-4 py-5 text-center">
              <p className="text-xs text-slate-500 mb-3">No brand profiles yet</p>
              <button
                onClick={() => { setOpen(false); onManage(); }}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-white rounded-lg text-xs font-semibold transition-colors"
                style={{ background: accentColor }}
              >
                <Palette size={12} />
                Create Brand
              </button>
            </div>
          ) : (
            <>
              <div className="max-h-60 overflow-y-auto py-1">
                {brands.map(brand => (
                  <button
                    key={brand.id}
                    onClick={() => { onSelect(brand); setOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-800 transition-colors text-left"
                  >
                    <div className="w-8 h-8 rounded-lg border border-slate-600 overflow-hidden bg-white flex items-center justify-center shrink-0">
                      {brand.logo_url ? (
                        <img src={brand.logo_url} alt="" className="w-full h-full object-contain p-0.5" />
                      ) : (
                        <Palette size={14} className="text-slate-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-medium text-slate-200 truncate">{brand.name}</span>
                        {brand.is_default && <Star size={10} className="text-purple-400 shrink-0" />}
                      </div>
                      {brand.agency_name && (
                        <span className="text-[10px] text-slate-500 truncate block">{brand.agency_name}</span>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <div className="w-3 h-3 rounded-full border border-slate-600" style={{ background: brand.primary_color }} />
                      <div className="w-3 h-3 rounded-full border border-slate-600" style={{ background: brand.secondary_color }} />
                    </div>
                  </button>
                ))}
              </div>
              <div className="border-t border-slate-800 p-2">
                <button
                  onClick={() => { setOpen(false); onManage(); }}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <Settings size={12} />
                  Manage Brands
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
